import * as React from 'react';
import { BrowserRouter, useNavigate, useLocation, NavigateFunction, Location } from 'react-router-dom';
import { css, Provider, ProviderContext, basePath, viewNode, headerNode, footerNode } from '../shared-exports';

type RouteAnimation = "slide-right" | "slide-left" | "slide-down" | "none"

interface Interface {
	/** The current browser location */
	location: Location
	/** The current active view */
	view: {
		/** The URL path to where this view exists */
		path: string
	},
	/** Keep tracks of the latest view displayed for each tab */
	tabs: {
		project: string,
		product: string
		edit: string
		settings: string,
		foto: string,
		ownproduct: string
	}
	/** Navigates to a given path and adds it to the route history */
	push: (path: string, animation: RouteAnimation) => void
	/** Navigates to a given path and replace the current path in history */
	replace: (path: string, animation: RouteAnimation) => void
	/** Navigates back to the most recent path in the route history and removes it */
	pop: (animation: RouteAnimation) => void
	addBeforeNavigateListener: (fn: (path: string) => void) => void
	removeBeforeNavigateListener: (fn: (path: string) => void) => void
}

interface Props {
	navigate: NavigateFunction
	location: Location
	children?: React.ReactNode
}

interface State {
	beforeNavigation: ((path: string) => void)[]
}

const DefaultContext: Interface = {
	view: {
		path: ""
	},
	tabs: {
		project: "/project",
		foto: "/foto",
		product: "/product",
		edit: "/product/edit",
		settings: "/settings",
		ownproduct: "/ritaproduct",
	},
	push: null!,
	pop: null!,
	replace: null!,
	location: null!,
	addBeforeNavigateListener: null!,
	removeBeforeNavigateListener: null!
}

/** The context that will be used by the provider */
export const RouteContext: React.Context<Interface> = React.createContext(DefaultContext);
export interface RouteContext extends ProviderContext<Interface> { };

/** Provider for accessing and manipulating the current url route and associated data */
export class RouteProvider extends Provider<Interface, Props, State> {
	/** Exposes the context to use */
	protected use() { return RouteContext }

	/** Declares the initial state */
	protected initialState(): Interface & State {
		return {
			...DefaultContext,
			location: this.props.location,
			view: {
				path: this.props.location.pathname,
			},
			push: this.push.bind(this),
			pop: this.pop.bind(this),
			replace: this.replace.bind(this),
			addBeforeNavigateListener: this.addListener.bind(this),
			removeBeforeNavigateListener: this.removeListener.bind(this),
			beforeNavigation: []
		}
	}

	private addListener(fn: (path: string) => void) {
		const listeners = this.state.beforeNavigation;
		listeners.push(fn)
		this.setState({beforeNavigation: listeners})
	}

	private removeListener(fn: (path: string) => void) {
		const listeners = this.state.beforeNavigation
			.filter(f => f !== fn);
		this.setState({beforeNavigation: listeners})
	}

	private animate(element: HTMLElement, animation: RouteAnimation, variation: "enter" | "leave" = "enter"): Promise<void> {
		return new Promise((resolve) => {
			resolve()
		})
	}

	private async navigate(animation: RouteAnimation, handler: () => void) {
		// Run all listeners
		for (const fn of this.state.beforeNavigation) {
			fn(this.props.location.pathname);
		}
		let element = document.getElementById(viewNode)
		if (!element) {
			return
		}
		// Animate out old route
		await this.animate(element, animation, "leave")
		// Route handler
		handler()
		// Animate in new route
		element = document.getElementById(viewNode)
		if (!element) {
			return
		}
		await this.animate(element, animation)
	}

	private async push(path: string, animation: RouteAnimation) {
		await this.updateTabsIndex(path);
		this.navigate(animation, () => {
			this.props.navigate(path);
			this.updateState({});
		})
	}

	private async pop(animation: RouteAnimation) {
		this.navigate(animation, () => {
			this.props.navigate(-1);
			this.updateTabsIndex(this.props.location.pathname);
		})
	}

	private async replace(path: string, animation: RouteAnimation) {
		await this.updateTabsIndex(path);
		this.navigate(animation, () => {
			this.props.navigate(path, { replace: true });
		})
	}

	/** Update the tab index of recent pages on navigation */
	private async updateTabsIndex(path: string) {
		const tabs = { ...this.state.tabs };
		if (path.indexOf("/project") > -1) {
			tabs.project = path;
		}
		else if (path.indexOf("/product") > -1) {
			tabs.product = path;
		}
		else if (path.indexOf("/settings") > -1) {
			tabs.settings = path;
		}
		else if (path.indexOf("/product/edit") > -1) {
			tabs.edit = path;
		}
		else if (path.indexOf("/foto") > -1) {
			tabs.foto = path;
		} else if (path.indexOf("/ritaproduct") > -1) {
			tabs.ownproduct = path;
		}
		await this.updateState({ tabs });
	}

	/** Updates the state of the provider manually */
	protected async updateState(newState: Partial<Interface> | Interface) {
		// Keep location in sync with props
		newState.location = this.props.location;
		// If no view has been set in the new state, default to the current location pathname
		newState.view = newState.view || {
			path: this.props.location.pathname,
		};
		await this.setState(newState as Interface);
	}

	/** Scroll to the top of the view when the route has changed */
	componentDidUpdate(prevProps: Props) {
		const element = document.getElementById(viewNode);
		if (element && this.props.location.pathname !== prevProps.location.pathname) {
			element.scrollTo(0, 0);
		}
	}

	/** Overridden provider render method for injecting the calculated state */
	public render() {
		const Context = this.use();
		return (
			<Context.Provider value={this.state} >
				<div className={css.AppWrapper}>
					<header id={headerNode}></header>
					{this.props.children}
					<footer id={footerNode}></footer>
				</div>
			</Context.Provider>
		);
	}
}

/** Functional bridge that provides v6 hooks to the class-based RouteProvider */
function RouteBridge({ children }: { children?: React.ReactNode }) {
	const navigate = useNavigate();
	const location = useLocation();
	return (
		<RouteProvider navigate={navigate} location={location}>
			{children}
		</RouteProvider>
	);
}

/** This router replaces the normal browser router and provides the correct props to the route provider */
export function ProviderRouter({ children }: { children?: React.ReactNode }) {
	return (
		<BrowserRouter basename={`/${basePath}`}>
			<RouteBridge>
				{children}
			</RouteBridge>
		</BrowserRouter>
	);
}
